import { useEffect } from "react";

/**
 * Per-route SEO + GEO helper.
 * Sets: title, description, keywords, canonical, hreflang, OG, Twitter,
 * BreadcrumbList JSON-LD, plus optional custom JSON-LD.
 */
const setMeta = (name, content, attr = "name") => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href, hreflang) => {
  if (!href) return;
  const sel = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const BASE = "https://lumiereevents.org";

export default function Seo({
  title,
  description,
  path = "/",
  image,
  keywords,
  jsonLd,
  breadcrumbs, // [{name, path}]
  ogType = "website",
}) {
  useEffect(() => {
    const url = BASE + path;
    const t =
      title ||
      "Lumière Entertainment — Luxury Event Entertainment in Athens & the Mediterranean";
    const d =
      description ||
      "Lumière Entertainment is a creative atelier producing luxury events in Athens. Bespoke entertainment, thematic concepts and integrated production for weddings, corporate events, hotels and destination events in Greece, Italy, France, Spain, UAE and worldwide.";
    const img = (image && (image.startsWith("http") ? image : BASE + image)) ||
      `${BASE}/generated/hero.jpg`;

    document.title = t;
    setMeta("description", d);
    if (keywords) setMeta("keywords", keywords);

    // Open Graph
    setMeta("og:title", t, "property");
    setMeta("og:description", d, "property");
    setMeta("og:url", url, "property");
    setMeta("og:image", img, "property");
    setMeta("og:type", ogType, "property");
    setMeta("og:locale", "el_GR", "property");
    setMeta("og:locale:alternate", "en_GB", "property");
    setMeta("og:site_name", "Lumière Entertainment", "property");

    // Twitter
    setMeta("twitter:title", t);
    setMeta("twitter:description", d);
    setMeta("twitter:image", img);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:creator", "@stam_kanta");

    // Canonical + hreflang
    setLink("canonical", url);
    setLink("alternate", url, "el-GR");
    setLink("alternate", url, "en");
    setLink("alternate", url, "x-default");

    // Robots
    setMeta(
      "robots",
      "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    );

    // Cleanup previous route-scoped JSON-LD nodes
    document
      .querySelectorAll('script[data-seo="route"]')
      .forEach((n) => n.parentNode && n.parentNode.removeChild(n));

    // BreadcrumbList JSON-LD
    if (breadcrumbs && breadcrumbs.length) {
      const bc = document.createElement("script");
      bc.type = "application/ld+json";
      bc.setAttribute("data-seo", "route");
      bc.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: BASE + b.path,
        })),
      });
      document.head.appendChild(bc);
    }

    // Custom page JSON-LD
    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      arr.forEach((obj) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-seo", "route");
        s.text = JSON.stringify(obj);
        document.head.appendChild(s);
      });
    }

    return () => {
      document
        .querySelectorAll('script[data-seo="route"]')
        .forEach((n) => n.parentNode && n.parentNode.removeChild(n));
    };
  }, [title, description, path, image, keywords, jsonLd, breadcrumbs, ogType]);

  return null;
}
